// One way to keep a library item, for every caller (the Save box, the DXF dialog).
// Returns the reason it was not kept, or null when it was. Never throws.
import type { LibraryItem } from "./library";
import { itemJson } from "./compact";
import { MAX_ITEM_CHARS, saveLocalItem } from "./store";

export type KeepWhere = "account" | "local";

export async function saveToLibrary(item: LibraryItem, where: KeepWhere): Promise<string | null> {
  const chars = itemJson(item).length;
  if (chars > MAX_ITEM_CHARS) {
    return `This object is too detailed to keep (${Math.round(chars / 1000)} thousand characters; the limit is ${Math.round(MAX_ITEM_CHARS / 1000)} thousand). Simplify the drawing in your CAD program, or place it without saving.`;
  }
  try {
    if (where === "account") {
      const eden = await import("../eden");
      const cfg = await eden.loadEdenConfig();
      if (!cfg) return "Eden is not connected here. Choose This computer.";
      await eden.saveAccountItem(cfg, item);
      return null;
    }
    return saveLocalItem(item) ? null : "This computer refused to store it (storage is full). Remove an object from the library, or keep this one in the account library.";
  } catch (e) {
    return `Not saved: ${(e as Error).message}`;
  }
}

/**
 * 3.30.0: put a saved object in another category, where it is kept (this computer, or the account library: the same
 * Eden record, so its id, its favourite star and everyone's copy follow). Owner, 2026-10-27: "you should be able to
 * move imported plans into different categories". Returns the reason it did not move, or null. Never throws.
 */
export async function moveToCategory(item: LibraryItem, where: KeepWhere, category: string, now = new Date().toISOString()): Promise<string | null> {
  const to = category.trim();
  if (!to) return "Choose a category, or type the name of a new one.";
  if (to === item.category) return `"${item.name}" is in ${to} already.`;
  const why = await saveToLibrary({ ...item, category: to, updatedAt: now }, where);
  return why ? why.replace(/^Not saved:/, "Not moved:") : null;
}

/** Every category that this computer's objects and the account's cached objects use. */
export function usedCategories(items: Iterable<LibraryItem>): string[] {
  return [...new Set([...items].map((i) => i.category))];
}
